import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Globe } from 'lucide-react';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const [selectedRole, setSelectedRole] = useState('');
  const [username, setUsername] = useState('info@rascasone.com');
  const [password, setPassword] = useState('Rascasone-Demo2024!');

  const roles = [
    { value: 'facilityManager', label: t('facilityManager') },
    { value: 'maintenanceTechnician', label: t('maintenanceTechnician') },
    { value: 'safetyOfficer', label: t('safetyOfficer') },
    { value: 'management', label: t('management') },
  ];

  const handleLogin = (e) => {
    e.preventDefault();
    if (selectedRole) {
      login(selectedRole, username);
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted to-primary-light flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center shadow-soft">
              <Building2 className="w-10 h-10 text-primary-foreground" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">{t('loginTitle')}</h1>
          <p className="text-muted-foreground">{t('loginSubtitle')}</p>
        </div>

        <Card className="shadow-elevated animate-fade-in">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>{t('login')}</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="gap-2"
              >
                <Globe className="w-4 h-4" />
                {language === 'en' ? 'CS' : 'EN'}
              </Button>
            </div>
            <CardDescription>{t('selectRole')}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">{t('loginHint')}</p>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="role">{t('selectRole')}</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger id="role">
                    <SelectValue placeholder={t('selectRole')} />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="username">{t('email')}</Label>
                <Input
                  id="username"
                  type="email"
                  placeholder="info@rascasone.com"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('password')}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <Button type="submit" className="w-full" disabled={!selectedRole}>
                {t('signIn')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
